import { keepPreviousData, queryOptions } from '@tanstack/react-query';
import type { OdataQueryBuilder } from 'odata-builder';

import type { DataTableQueryOptions } from '@apple/ui/data-table/types';
import { createDataTableQueryOptionsSchema } from '@apple/ui/data-table/utils/filters';
import { applyOdataSorting } from '@apple/ui/data-table/utils/sorting';
import {
	DEFAULT_ODATA_PAGE_STATE,
	escapeODataFilterValue,
	execODataQuery,
} from '@apple/utils/api/odata';

import { budgetKeys } from '@apple/features/store/queries/budget';

import {
	type BudgetFilters,
	type BudgetListingModel,
	budgetFiltersSchema,
	budgetListingModelSchema,
} from '../models/budget';
import type { StoreFilters, StoreOdataListing } from '../models/listing';
import { storeFiltersSchema, storeOdataListingSchema } from '../models/listing';

const storeQueryOptionsSchema = createDataTableQueryOptionsSchema<StoreFilters>(storeFiltersSchema);

/** @see AppleRetail.Web.Features.Stores.StoreManagementODataController */
export function getStoreDataTableQueryOptions(options: DataTableQueryOptions<StoreFilters>) {
	const { pagination, filters, sorting } = storeQueryOptionsSchema.parse(options);
	return queryOptions({
		queryKey: ['stores-odata', filters, sorting, pagination],
		queryFn: async ({ signal }) =>
			await execODataQuery<StoreOdataListing>({
				urlPath: '/odata/management/stores',
				rowSchema: storeOdataListingSchema,
				builderFn: odataBuilder =>
					buildStoreODataQuery(odataBuilder, options.filters, sorting),
				pagination,
				signal,
			}),
		placeholderData: keepPreviousData,
		throwOnError: true,
		retry: false,
		initialData: DEFAULT_ODATA_PAGE_STATE,
	});
}

// TODO: Find a better way to map filters and sorting to the odata builder
function buildStoreODataQuery(
	builder: OdataQueryBuilder<StoreOdataListing>,
	filters: StoreFilters,
	sorting: { id: string; desc: boolean }[] | undefined,
) {
	applyOdataSorting<StoreOdataListing>({
		builder,
		map: {
			addressLine3: 'streetAddress3',
		},
		sorting,
		defaultSort: [{ field: 'name', orderDirection: 'asc' }],
	});

	if (filters.name !== undefined) {
		builder.filter({
			field: 'name',
			operator: 'contains',
			value: escapeODataFilterValue(filters.name),
			ignoreCase: true,
		});
	}

	if (filters.thirdPartyId !== undefined) {
		builder.filter({
			field: 'thirdPartyId',
			operator: 'contains',
			value: filters.thirdPartyId,
			ignoreCase: true,
		});
	}

	if (filters.customerGroupName !== undefined) {
		builder.filter({
			field: 'customerGroupName',
			operator: 'contains',
			value: escapeODataFilterValue(filters.customerGroupName),
		});
	}

	if (filters.code !== undefined) {
		builder.filter({
			field: 'code',
			operator: 'contains',
			value: escapeODataFilterValue(filters.code),
			ignoreCase: true,
		});
	}

	if (filters.status !== undefined) {
		builder.filter({
			field: 'status',
			operator: 'eq',
			value: filters.status,
		});
	}

	if (filters.streetAddress1 !== undefined) {
		builder.filter({
			field: 'streetAddress1',
			operator: 'contains',
			value: escapeODataFilterValue(filters.streetAddress1),
			ignoreCase: true,
		});
	}

	if (filters.streetAddress2 !== undefined) {
		builder.filter({
			field: 'streetAddress2',
			operator: 'contains',
			value: escapeODataFilterValue(filters.streetAddress2),
			ignoreCase: true,
		});
	}

	if (filters.stateOrProvince !== undefined) {
		builder.filter({
			field: 'stateOrProvince',
			operator: 'contains',
			value: escapeODataFilterValue(filters.stateOrProvince),
			ignoreCase: true,
		});
	}

	if (filters.city !== undefined) {
		builder.filter({
			field: 'city',
			operator: 'contains',
			value: escapeODataFilterValue(filters.city),
			ignoreCase: true,
		});
	}

	if (filters.postalCode !== undefined) {
		builder.filter({
			field: 'postalCode',
			operator: 'contains',
			value: escapeODataFilterValue(filters.postalCode),
			ignoreCase: true,
		});
	}

	if (filters.countryCode !== undefined) {
		builder.filter({
			field: 'countryCode',
			operator: 'eq',
			value: filters.countryCode,
		});
	}

	if (filters.locationType !== undefined) {
		builder.filter({
			field: 'locationType',
			operator: 'eq',
			value: filters.locationType,
		});
	}
}

const budgetQueryOptionsSchema =
	createDataTableQueryOptionsSchema<BudgetFilters>(budgetFiltersSchema);

export function budgetsOdataQueryOptions(options: DataTableQueryOptions<BudgetFilters>) {
	const { pagination, filters, sorting } = budgetQueryOptionsSchema.parse(options);
	return queryOptions({
		queryKey: budgetKeys.listPage(pagination, sorting, filters),
		queryFn: async ({ signal }) =>
			await execODataQuery<BudgetListingModel>({
				urlPath: '/odata/budgets',
				rowSchema: budgetListingModelSchema,
				builderFn: odataBuilder => buildBudgetODataQuery(odataBuilder, filters, sorting),
				pagination,
				signal,
			}),
		placeholderData: keepPreviousData,
		throwOnError: true,
		initialData: DEFAULT_ODATA_PAGE_STATE,
		retry: false,
	});
}

function buildBudgetODataQuery(
	builder: OdataQueryBuilder<BudgetListingModel>,
	filters: BudgetFilters,
	sorting: { id: string; desc: boolean }[] | undefined,
) {
	applyOdataSorting<BudgetListingModel>({
		builder,
		sorting,
	});

	if (filters.warehouseCode !== undefined) {
		builder.filter({
			field: 'warehouseCode',
			operator: 'eq',
			value: filters.warehouseCode,
		});
	}

	if (filters.periodNumber !== undefined && Number(filters.periodNumber) !== -1) {
		builder.filter({
			field: 'periodNumber',
			operator: 'eq',
			value: Number(filters.periodNumber),
		});
	}
	if (filters.fiscalYear !== undefined) {
		builder.filter({
			field: 'fiscalYear',
			operator: 'eq',
			value: Number(filters.fiscalYear),
		});
	}
	if (filters.countryCode !== undefined) {
		builder.filter({
			field: 'countryCode',
			operator: 'eq',
			value: filters.countryCode,
		});
	}
	if (filters.storeIds && filters.storeIds.length > 0) {
		builder.filter({
			filters: filters.storeIds.map(id => ({
				field: 'storeId',
				operator: 'eq',
				value: id,
				ignoreCase: true,
			})),
			logic: 'or',
		});
	}
	if (filters.isOverBudget !== undefined) {
		builder.filter({
			field: 'isOverBudget',
			operator: 'eq',
			value: filters.isOverBudget,
		});
	}
}
